/**
 * Hero Search Web Component
 * A reusable search component for filtering helpful topics and managing section visibility
 */
class HeroSearch extends HTMLElement {
  constructor() {
    super();
    
    // Component configuration with default values
    this.config = {
      debounce: parseInt(this.getAttribute('data-debounce')) || 150,
      minLength: parseInt(this.getAttribute('data-min-length')) || 1,
      highlight: this.getAttribute('data-highlight') === 'true',
      announce: this.getAttribute('data-announce') === 'true',
      keyboard: this.getAttribute('data-keyboard') === 'true'
    };
    
    // Component state
    this.state = {
      searchTerm: '',
      isSearching: false,
      originalDisplayValues: new Map(),
      noResultsMessage: null,
      faqNoContentMessage: null
    };
    
    // Bind methods to maintain context
    this.performSearch = this.performSearch.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.initializeComponent();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  /**
   * Initialize the component
   */
  initializeComponent() {
    const heroSearchInput = this.querySelector('[data-hero-search-input]');
    
    if (!heroSearchInput) {
      console.warn('HeroSearch: No search input found');
      return;
    }

    // Find target elements
    this.elements = {
      input: heroSearchInput,
      helpfulTopicsGrid: document.querySelector('#helpful-topics-grid'),
      helpfulItems: document.querySelectorAll('.helpful-item'),
      faqCardsGrid: document.querySelector('.faq-cards-grid'),
      faqCards: document.querySelectorAll('.faq-card'),
      allSections: this.getAllSections()
    };


    // Store original display values
    this.storeOriginalDisplayValues();
    
    // Create no results message
    this.createNoResultsMessage();
    
    // Create FAQ no content message
    this.createFaqNoContentMessage();
    
    // Setup event listeners
    this.setupEventListeners();
    
    // Announce component initialization
    if (this.config.announce) {
      this.announce('Search component initialized');
    }
  }

  /**
   * Get all sections that should be managed by the search
   */
  getAllSections() {
    const mainContent = document.querySelector(
      '.MainContent, #MainContent, [class*="MainContent"], [id*="MainContent"]'
    );
    
    // Get all sections, including those with shopify-section class
    const allSections = mainContent
      ? mainContent.querySelectorAll('section, .shopify-section')
      : document.querySelectorAll('section, .shopify-section');
    
    // Filter out the hero search section itself
    return Array.from(allSections).filter(section => {
      const isHeroSearch = this.isHeroSearchSection(section);
      return !isHeroSearch;
    });
  }

  /**
   * Store original display values for sections
   */
  storeOriginalDisplayValues() {
    // Store original values for specific sections we want to hide
    const sectionsToTrack = [
      '.contact-page-main-content',
      // Add more specific selectors here if needed
    ];
    
    sectionsToTrack.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        this.state.originalDisplayValues.set(element, element.style.display || '');
      });
    });
    
    // Store original display values for FAQ cards
    this.elements.faqCards.forEach(card => {
      this.state.originalDisplayValues.set(card, card.style.display || '');
    });
  }

  /**
   * Create and insert no results message
   */
  createNoResultsMessage() {
    if (this.state.noResultsMessage) return;

    this.state.noResultsMessage = document.createElement('div');
    this.state.noResultsMessage.id = 'no-results-message';
    this.state.noResultsMessage.className = 'no-results-message';
    this.state.noResultsMessage.style.display = 'none';
    this.state.noResultsMessage.innerHTML = 
      '<p>No topics found matching your search. Try a different search term.</p>';
    
    if (this.elements.helpfulTopicsGrid) {
      this.elements.helpfulTopicsGrid.parentNode.insertBefore(
        this.state.noResultsMessage,
        this.elements.helpfulTopicsGrid.nextSibling
      );
    }
  }

  /**
   * Create and insert FAQ no content message
   */
  createFaqNoContentMessage() {
    if (this.state.faqNoContentMessage) return;

    this.state.faqNoContentMessage = document.createElement('div');
    this.state.faqNoContentMessage.id = 'faq-no-content-message';
    this.state.faqNoContentMessage.className = 'faq-no-content-message';
    this.state.faqNoContentMessage.style.display = 'none';
    this.state.faqNoContentMessage.innerHTML = 
      '<p>No FAQ content found matching your search.</p>';
    
    if (this.elements.faqCardsGrid) {
      this.elements.faqCardsGrid.parentNode.insertBefore(
        this.state.faqNoContentMessage,
        this.elements.faqCardsGrid.nextSibling
      );
    }
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Input event with debouncing
    this.elements.input.addEventListener('input', this.handleInput);
    
    // Keyboard navigation
    if (this.config.keyboard) {
      this.elements.input.addEventListener('keydown', this.handleKeydown);
    }
  }

  /**
   * Handle input events with debouncing
   */
  handleInput(event) {
    const searchTerm = event.target.value;
    
    // Clear existing timeout
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    
    // Set new timeout
    this.debounceTimeout = setTimeout(() => {
      this.performSearch(searchTerm);
    }, this.config.debounce);
  }

  /**
   * Handle keyboard events
   */
  handleKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.performSearch(event.target.value);
    }
  }

  /**
   * Perform the search operation
   */
  performSearch(searchTerm) {
    this.state.searchTerm = searchTerm;
    this.state.isSearching = searchTerm.trim() !== '';
    
    let visibleCount = 0;

    // Handle section visibility
    this.handleSectionVisibility(searchTerm);
    
    // Handle helpful topics filtering
    visibleCount = this.filterHelpfulTopics(searchTerm);
    
    // Handle FAQ cards filtering
    const faqVisibleCount = this.filterFaqCards(searchTerm);
    
    // Show/hide no results message
    this.toggleNoResultsMessage(visibleCount, searchTerm);
    
    // Show/hide FAQ no content message
    this.toggleFaqNoContentMessage(faqVisibleCount, searchTerm);
    
    // Announce results if enabled
    if (this.config.announce) {
      this.announceSearchResults(visibleCount, searchTerm);
    }
  }

  /**
   * Handle section visibility based on search term
   */
  handleSectionVisibility(searchTerm) {
    
    if (searchTerm.trim() !== '') {
      // Only hide specific sections we want to hide
      const sectionsToHide = [
        '.contact-page-main-content',
        // Add more specific selectors here if needed
      ];
      
      sectionsToHide.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          element.style.display = 'none';
        });
      });
    } else {
      // Show all sections when search is empty
      this.elements.allSections.forEach(section => {
        const originalDisplay = this.state.originalDisplayValues.get(section);
        section.style.display = originalDisplay;
      });
      
      // Restore specific sections
      const sectionsToRestore = [
        '.contact-page-main-content',
        // Add more specific selectors here if needed
      ];
      
      sectionsToRestore.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          const originalDisplay = this.state.originalDisplayValues.get(element);
          element.style.display = originalDisplay || '';
        });
      });
    }
  }

  /**
   * Check if section is helpful topics section
   */
  isHelpfulTopicsSection(section) {
    return section.classList.contains('helpful-topics') ||
           section.querySelector('.helpful-topics') ||
           section.id === 'helpful-topics-grid' ||
           section.querySelector('#helpful-topics-grid');
  }

  /**
   * Check if section is newsletter section
   */
  isNewsletterSection(section) {
    return section.id === 'shopify-section-newsletter-section' ||
           section.classList.contains('shopify-section-newsletter-section') ||
           section.querySelector('#shopify-section-newsletter-section');
  }

  /**
   * Check if section is hero search section
   */
  isHeroSearchSection(section) {
    // Check if this section contains the hero-search custom element
    const hasHeroSearchElement = section.querySelector('hero-search') || section.tagName === 'HERO-SEARCH';
    
    // Check for hero-search-section class
    const hasHeroSearchClass = section.classList.contains('hero-search-section');
    
    // Check if this section is the parent of hero-search element
    const isParentOfHeroSearch = section.closest('.hero-search-section') || 
                                 section.querySelector('.hero-search-section');
    
    const isHeroSearch = hasHeroSearchElement || hasHeroSearchClass || isParentOfHeroSearch;
    
    return isHeroSearch;
  }

  /**
   * Check if section is contact page content section
   */
  isContactPageContentSection(section) {
    return section.classList.contains('contact-page-main-content') ||
           section.querySelector('.contact-page-main-content');
  }

  /**
   * Filter helpful topics based on search term
   */
  filterHelpfulTopics(searchTerm) {
    let visibleCount = 0;

    this.elements.helpfulItems.forEach(item => {
      const topicTitle = item.getAttribute('data-topic-title') || '';
      const topicLabel = item.querySelector('span')?.textContent || '';
      const searchText = (topicTitle + ' ' + topicLabel).toLowerCase();

      if (searchTerm === '' || searchText.includes(searchTerm.toLowerCase())) {
        item.style.display = 'block';
        item.classList.remove('hidden');
        item.setAttribute('aria-hidden', 'false');
        visibleCount++;
      } else {
        item.style.display = 'none';
        item.classList.add('hidden');
        item.setAttribute('aria-hidden', 'true');
      }
    });

    return visibleCount;
  }

  /**
   * Filter FAQ cards based on search term
   */
  filterFaqCards(searchTerm) {
    let visibleCount = 0;

    this.elements.faqCards.forEach(card => {
      const cardText = card.textContent || '';
      const searchText = cardText.toLowerCase();

      if (searchTerm === '' || searchText.includes(searchTerm.toLowerCase())) {
        card.style.display = this.state.originalDisplayValues.get(card) || 'block';
        card.classList.remove('hidden');
        card.setAttribute('aria-hidden', 'false');
        visibleCount++;
      } else {
        card.style.display = 'none';
        card.classList.add('hidden');
        card.setAttribute('aria-hidden', 'true');
      }
    });

    return visibleCount;
  }

  /**
   * Toggle no results message visibility
   */
  toggleNoResultsMessage(visibleCount, searchTerm) {
    if (this.state.noResultsMessage) {
      this.state.noResultsMessage.style.display = 
        (visibleCount === 0 && searchTerm !== '') ? 'block' : 'none';
    }
  }

  /**
   * Toggle FAQ no content message visibility
   */
  toggleFaqNoContentMessage(visibleCount, searchTerm) {
    if (this.state.faqNoContentMessage) {
      this.state.faqNoContentMessage.style.display = 
        (visibleCount === 0 && searchTerm !== '') ? 'block' : 'none';
    }
  }

  /**
   * Announce search results for accessibility
   */
  announceSearchResults(visibleCount, searchTerm) {
    if (searchTerm === '') {
      this.announce('All topics shown');
    } else if (visibleCount === 0) {
      this.announce('No topics found matching your search');
    } else {
      this.announce(`${visibleCount} topics found`);
    }
  }

  /**
   * Announce message for screen readers
   */
  announce(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.style.position = 'absolute';
    announcement.style.left = '-10000px';
    announcement.style.width = '1px';
    announcement.style.height = '1px';
    announcement.style.overflow = 'hidden';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  /**
   * Cleanup component
   */
  cleanup() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    
    if (this.elements && this.elements.input) {
      this.elements.input.removeEventListener('input', this.handleInput);
      this.elements.input.removeEventListener('keydown', this.handleKeydown);
    }
  }

  /**
   * Public method to get current search term
   */
  getSearchTerm() {
    return this.state.searchTerm;
  }

  /**
   * Public method to clear search
   */
  clearSearch() {
    if (this.elements && this.elements.input) {
      this.elements.input.value = '';
      this.performSearch('');
    }
  }

  /**
   * Public method to set search term
   */
  setSearchTerm(term) {
    if (this.elements && this.elements.input) {
      this.elements.input.value = term;
      this.performSearch(term);
    }
  }
}

// Register the custom element
document.addEventListener('DOMContentLoaded', () => {
  if (!customElements.get('hero-search')) {
    customElements.define('hero-search', HeroSearch);
  }
});

// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = HeroSearch;
}
